import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, AlertCircle, CheckSquare, XSquare, FileText } from 'lucide-react';
import { API } from '@/config/api';

export default function RequestsPage({ token, user }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [reaccessRequests, setReaccessRequests] = useState([]);
  const [reaccessLoading, setReaccessLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchReaccessRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/access/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
    } catch (error) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchReaccessRequests = async () => {
    try {
      setReaccessLoading(true);
      const response = await axios.get(`${API}/access/reaccess-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const all = response.data.requests || [];

      // Group by udt_id + viewer_id and keep only the latest (highest requested_at)
      const latestByKey = {};
      for (const r of all) {
        const key = `${r.udt_id || ''}::${r.viewer_id || ''}`;
        if (!key) continue;

        const current = latestByKey[key];
        if (!current) {
          latestByKey[key] = r;
        } else {
          const currTs = current.requested_at ? Date.parse(current.requested_at) : 0;
          const newTs = r.requested_at ? Date.parse(r.requested_at) : 0;
          if (newTs >= currTs) {
            latestByKey[key] = r;
          }
        }
      }

      setReaccessRequests(Object.values(latestByKey));
    } catch (error) {
      toast.error('Failed to fetch reaccess requests');
    } finally {
      setReaccessLoading(false);
    }
  };

  const handleApprove = async (requestId, action) => {
    try {
      const response = await axios.post(`${API}/access/approve`, 
        { request_id: requestId, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (action === 'approve') {
        toast.success(`Access approved! Share this token: ${response.data.token}`);
      } else {
        toast.success('Access rejected');
      }
      
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) {
      toast.error('Please select at least one request');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/access/bulk-approve`,
        selectedRequests,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Approved ${response.data.approved_count} request(s)`);
      setSelectedRequests([]);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedRequests.length === 0) {
      toast.error('Please select at least one request');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/access/bulk-reject`,
        selectedRequests,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Rejected ${response.data.rejected_count} request(s)`);
      setSelectedRequests([]);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk reject failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleRequest = (requestId) => {
    setSelectedRequests(prev =>
      prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const toggleAll = () => {
    if (selectedRequests.length === requests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(requests.map(r => r.request_id));
    }
  };

  const handleRespond = async (index, action) => {
    const req = reaccessRequests[index];
    console.log('[DEBUG] index:', index, 'req:', req);

    try {
      await axios.post(
        `${API}/access/reaccess-respond`,
        {
          request_id: req?.request_id,
          udt_id: req?.udt_id,
          viewer_id: req?.viewer_id,
          action,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update reaccessRequests state immediately - hide buttons, show status
      setReaccessRequests(prev => {
        const updated = [...prev];
        if (index >= 0 && index < updated.length) {
          updated[index] = {
            ...updated[index],
            status: action === 'approve' ? 'approved' : 'rejected',
          };
        }
        return updated;
      });
    } catch (e) {
      console.log('[DEBUG] handleRespond error:', e);
      alert('Failed. Try again.');
    }
  };

  return (
    <DashboardLayout user={user} token={token}>
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8 w-full min-w-0">
        <div className="flex items-center gap-4 mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" style={{ color: '#0F52BA' }}>Access Requests</h1>
            <p className="text-xs sm:text-sm" style={{ color: '#666' }}>Manage pending access requests for your documents</p>
          </div>
        </div>

        {requests.length > 0 && (
          <div className="max-w-3xl mx-auto mb-4">
            <Card>
              <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2 sm:gap-4">
                    <Checkbox
                      checked={selectedRequests.length === requests.length}
                      onCheckedChange={toggleAll}
                    />
                    <span className="text-xs sm:text-sm font-semibold">
                      {selectedRequests.length} of {requests.length} selected
                    </span>
                  </div>
                  {selectedRequests.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleBulkApprove}
                        disabled={loading}
                        style={{ background: '#10b981' }}
                      >
                        <CheckSquare className="w-4 h-4 mr-1" />
                        Approve Selected ({selectedRequests.length})
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleBulkReject}
                        disabled={loading}
                        style={{ borderColor: '#ef4444', color: '#ef4444' }}
                      >
                        <XSquare className="w-4 h-4 mr-1" />
                        Reject Selected ({selectedRequests.length})
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="space-y-4 max-w-3xl mx-auto">
          {requests.map((request) => (
            <Card key={request.request_id} className={selectedRequests.includes(request.request_id) ? 'border-2 border-blue-500' : ''}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedRequests.includes(request.request_id)}
                    onCheckedChange={() => toggleRequest(request.request_id)}
                  />
                  <div className="flex-1">
                    <CardTitle className="text-lg">Access Request</CardTitle>
                    <CardDescription>UDT ID: {request.udt_id}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-semibold">Requester: {request.requester_name}</p>
                  <p className="text-sm" style={{ color: '#666' }}>{request.requester_email}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold">Purpose:</p>
                  <p className="text-sm" style={{ color: '#666' }}>{request.purpose}</p>
                </div>
                <p className="text-sm" style={{ color: '#666' }}>
                  Requested: {new Date(request.created_at).toLocaleString()}
                </p>
                {request.status === 'pending' ? (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      onClick={() => handleApprove(request.request_id, 'approve')}
                      style={{
                        padding: '8px 14px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleApprove(request.request_id, 'reject')}
                      style={{
                        padding: '8px 14px',
                        background: 'white',
                        border: '1px solid #ef4444',
                        color: '#ef4444',
                        borderRadius: '6px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      ✗ Reject
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      display: 'inline-block',
                      fontWeight: 600,
                      background:
                        request.status === 'approved' ? '#dcfce7' : '#fee2e2',
                      color: request.status === 'approved' ? '#16a34a' : '#dc2626',
                    }}
                  >
                    {request.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {requests.length === 0 && !loading && reaccessRequests.length === 0 && !reaccessLoading && (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
              <p style={{ color: '#666' }}>No pending requests</p>
            </div>
          )}

          {reaccessRequests.length > 0 && (
            <div className="mt-8 space-y-4">
              <h2 className="text-lg font-semibold" style={{ color: '#0F52BA' }}>
                Reaccess Requests
              </h2>
              {reaccessRequests.map((req, index) => {
                const status = (req.status || 'pending').toLowerCase();
                const borderClass =
                  status === 'approved'
                    ? 'border-green-400'
                    : status === 'rejected'
                      ? 'border-red-400'
                      : status === 'revoked'
                        ? 'border-yellow-400'
                        : 'border-orange-400';

                let statusBadge = null;
                if (status === 'approved') {
                  statusBadge = (
                    <span className="bg-green-100 text-green-700 rounded-full px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
                      ✅ Approved
                    </span>
                  );
                } else if (status === 'rejected') {
                  statusBadge = (
                    <span className="bg-red-100 text-red-700 rounded-full px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
                      ❌ Rejected
                    </span>
                  );
                } else if (status === 'revoked') {
                  statusBadge = (
                    <span className="bg-yellow-100 text-yellow-700 rounded-full px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
                      ⚠️ Revoked
                    </span>
                  );
                }

                return (
                  <Card
                    key={req.request_id}
                    className={`w-full max-w-4xl mx-auto border-l-4 ${borderClass} shadow-md hover:shadow-lg transition`}
                  >
                    <CardContent className="px-6 py-4">
                      <div className="flex items-center justify-between gap-4">
                        {/* Left: document + viewer info */}
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 flex-shrink-0">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-semibold text-sm text-gray-900 truncate">
                              {req.document_name || req.udt_id}
                            </span>
                            <span className="text-xs text-gray-600 truncate">
                              {req.viewer_name || req.viewer_id}
                              {req.viewer_email ? ` • ${req.viewer_email}` : ''}
                            </span>
                          </div>
                        </div>

                        {/* Middle: violation reason pill */}
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center bg-amber-100 text-amber-700 rounded-full px-3 py-1 text-xs font-semibold max-w-xs truncate">
                            ⚠️ {req.violation_reason || 'Unknown'}
                          </span>
                        </div>

                        {/* Right: timestamp + actions / status badge */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-500">
                            {req.requested_at
                              ? new Date(req.requested_at).toLocaleString()
                              : '-'}
                          </span>
                          {status === 'pending' ? (
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                style={{ background: '#10b981' }}
                                onClick={() => handleRespond(index, 'approve')}
                                disabled={reaccessLoading}
                              >
                                ✓ Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                style={{ borderColor: '#ef4444', color: '#ef4444' }}
                                onClick={() => handleRespond(index, 'reject')}
                                disabled={reaccessLoading}
                              >
                                ✗ Reject
                              </Button>
                            </div>
                          ) : (
                            statusBadge
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}